/**
 * AgentChatSession (open_file / onToolCall) mounts when a project id exists.
 * Idle Think hydrate fills get-messages — it is not a mount gate. #145 left
 * hydrate=false on ThinkGuide-only and blocked tools during `?file=`.
 */
export function thinkSessionMounts(projectId: string | undefined | null): boolean {
  return typeof projectId === "string" && projectId.trim().length > 0;
}

/**
 * Official `requestIdleCallback` deadline so history still hydrates when
 * the main thread is busy. Not a session-mount gate (no 4s / 800ms
 * wait-for-fetch). Not coupled to Files prefetch.
 */
export const THINK_HYDRATE_IDLE_DEADLINE_MS = 2_000;

export type ThinkHydrateMessage = { id?: string; role?: string };

/**
 * useAgentChat onToolCall only reads tool parts on the last assistant
 * message (`input-available`). get-messages must not be written into
 * `chat.messages` (older pages are a separate `olderPages` list). Applying
 * history during a live turn can leave that last row as the user message —
 * open_file never reaches MissionNavigator, so `?file=` stays empty.
 */
export function thinkHydrateBlocksOnToolCall(
  messages: readonly ThinkHydrateMessage[] | null | undefined,
  status?: string | null,
): boolean {
  if (status === "error") return false;
  if (status === "submitted" || status === "streaming") return true;
  const rows = Array.isArray(messages) ? messages : [];
  const last = rows[rows.length - 1];
  return last?.role === "user";
}

/**
 * Official `requestIdleCallback` for Think history. Do not bind to inspect
 * / `pcbx:files-viewer-fetch` / revision-file prefetch — those dual tracks
 * put get-messages on the post-file Files window. Timeout so history still
 * fills if the main thread stays busy. Do not use this to delay mounting
 * the session.
 */
export function scheduleThinkHydrate(arm: () => void): () => void {
  let done = false;
  const fire = () => {
    if (done) return;
    done = true;
    arm();
  };
  if (typeof requestIdleCallback === "function") {
    const idleId = requestIdleCallback(fire, { timeout: THINK_HYDRATE_IDLE_DEADLINE_MS });
    return () => {
      done = true;
      if (typeof cancelIdleCallback === "function") cancelIdleCallback(idleId);
    };
  }
  const timeoutId = setTimeout(fire, 0);
  return () => {
    done = true;
    clearTimeout(timeoutId);
  };
}
